import React from "react";
import { Box, Typography, Button, useTheme } from "@mui/material";
import { useTranslation } from "react-i18next";
import { Icon } from "@iconify/react";
import StyledSelect from "../../../components/Fields/SingleSelect";
import StyledTextArea from "../../../components/Fields/TextArea";
import MultipleFilesSection from "./MultipleFilesSection";
import type { FormProps } from "../types";
import type { UserDocumentResponse } from "../../../services";

interface AIInsightFormProps extends FormProps {
  analysisType: string;
  onAnalysisTypeChange: (value: string) => void;
  customInstructions: string;
  onCustomInstructionsChange: (value: string) => void;
  multipleFiles: File[];
  multipleSelectedDocuments: UserDocumentResponse[];
  onFileRemove: (index: number) => void;
  onDocumentDeselect: (documentId: string) => void;
  onFileUpload: (files: FileList) => void;
  onClearAll: () => void;
  onDownloadDocument: (document: UserDocumentResponse) => void;
}

const AIInsightForm: React.FC<AIInsightFormProps> = ({
  analysisType,
  onAnalysisTypeChange,
  customInstructions,
  onCustomInstructionsChange,
  multipleFiles,
  multipleSelectedDocuments,
  onFileRemove,
  onDocumentDeselect,
  onFileUpload,
  onClearAll,
  onDownloadDocument,
  isProcessing,
  isSmallScreen,
  onOpenBottomDrawer,
}) => {
  const theme = useTheme();
  const { t } = useTranslation();

  return (
    <>
      <StyledSelect
        label={t("aiInsight.multiUpload.analysisTypeLabel")}
        value={analysisType}
        onChange={(event) => onAnalysisTypeChange(event.target.value as string)}
        options={[
          { label: t("aiInsight.multiUpload.analysisTypeSummary"), value: "DocumentSamenvatting" },
          { label: t("aiInsight.multiUpload.analysisTypeDataExtraction"), value: "GegevensExtractie" },
          { label: t("aiInsight.multiUpload.analysisTypeQuestion"), value: "Question" },
        ]}
        disabled={isProcessing}
      />

      {isSmallScreen && (
        <Box display={"flex"} flexDirection={"column"} gap={1}>
          <Typography variant="h5">{t("aiInsight.multiUpload.uploadSectionTitle")}</Typography>
          <Button
            size="large"
            variant="outlined"
            color="primary"
            onClick={onOpenBottomDrawer}
            endIcon={
              <Icon
                icon="hugeicons:arrow-up-right-01"
                width={24}
                height={24}
                color={theme.palette.primary.main}
              />
            }
          >
            {t("aiInsight.multiUpload.availableSpec")}
          </Button>
        </Box>
      )}

      <StyledTextArea
        placeholder={
          analysisType === "Question"
            ? t("aiInsight.multiUpload.questionPlaceholder")
            : t("aiInsight.multiUpload.customInstructionsPlaceholder")
        }
        rows={3}
        value={customInstructions}
        onChange={(e) => onCustomInstructionsChange(e.target.value)}
        disabled={isProcessing}
        label={analysisType === "Question" ? t("aiInsight.multiUpload.questionLabel") : undefined}
        required={analysisType === "Question"}
      />

      {/* Document Display - Always allow multiple document selection */}
      <MultipleFilesSection
        multipleFiles={multipleFiles}
        multipleSelectedDocuments={multipleSelectedDocuments}
        onFileRemove={onFileRemove}
        onDocumentDeselect={onDocumentDeselect}
        onFileUpload={onFileUpload}
        onClearAll={onClearAll}
        onDownloadDocument={onDownloadDocument}
        isProcessing={isProcessing}
        isSmallScreen={isSmallScreen}
        maxFiles={5}
      />
    </>
  );
};

export default AIInsightForm;

